import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../pages/AuthContext';
import "../styles/OZShops.css";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
    faStore, 
    faUsers, 
    faPlus, 
    faEdit, 
    faTrash, 
    faUserPlus, 
    faCheck,
    faTimes,
    faPaintBrush,
    faSave,
    faExclamationTriangle,
    faCheckCircle,
    faBuilding,
    faIdCard,
    faHashtag,
    faMapMarkerAlt,
    faUser,
    faCalendarAlt,
    faEye,
    faMoon,
    faSun,
    faChartLine,
    faBoxes,
    faTruck,
    faCog,
    faPalette,
    faGlobe,
    faLanguage,
    faShoppingCart,
    faWarehouse,
    faStoreAlt,
    faUsersCog,
    faUserCheck,
    faClock,
    faShieldAlt,
    faRocket,
    faStar,
    faGem,
    faSparkles
} from '@fortawesome/free-solid-svg-icons';

// ==============================================
// Translation Utility for Shop Management
// ==============================================
const createOZShopsTranslator = () => {
    let cache = new Map();
    let translationListeners = new Set();

    const loadFromCache = () => {
        try {
            const cached = localStorage.getItem('ozshops_translations');
            if (cached) {
                const parsed = JSON.parse(cached);
                Object.entries(parsed).forEach(([key, value]) => {
                    cache.set(key, value);
                });
            }
        } catch (e) {
            console.log("No cache found in OZ Shops");
        }
    };

    const saveToCache = () => {
        try {
            const cacheObj = {};
            cache.forEach((value, key) => {
                cacheObj[key] = value;
            });
            localStorage.setItem('ozshops_translations', JSON.stringify(cacheObj));
        } catch (e) {
            console.error("Error saving to cache:", e);
        }
    };

    const translateAPI = async (text, targetLang) => {
        if (targetLang === 'en') return text;

        const dict = {
            'ur': {
                'Shop Management': 'دکان کا انتظام',
                'Manage Shops & Users': 'دکانیں اور صارفین کا انتظام کریں',
                'Create New Shop': 'نئی دکان بنائیں',
                'Edit Shop': 'دکان میں ترمیم کریں',
                'Shop Name': 'دکان کا نام',
                'Business Module': 'کاروباری ماڈیول',
                'Shop Address': 'دکان کا پتہ',
                'Theme Color': 'تھیم کا رنگ',
                'Status': 'حالت',
                'Active': 'فعال',
                'Inactive': 'غیر فعال',
                'Created By': 'تخلیق کردہ',
                'Shop List': 'دکانوں کی فہرست',
                'Total Shops': 'کل دکانیں',
                'Delete Shop': 'دکان حذف کریں',
                'Assign User': 'صارف تفویض کریں',
                'Select User': 'صارف منتخب کریں',
                'Select Shop': 'دکان منتخب کریں',
                'Save Shop': 'دکان محفوظ کریں',
                'Update Shop': 'دکان اپ ڈیٹ کریں',
                'Cancel': 'منسوخ کریں',
                'Delete': 'حذف کریں',
                'Edit': 'ترمیم کریں',
                'Creating...': 'بن رہا ہے...',
                'Updating...': 'اپ ڈیٹ ہو رہا ہے...',
                'Assigning...': 'تفویض ہو رہا ہے...',
                'Shop created successfully': 'دکان کامیابی سے بن گئی',
                'Shop updated successfully': 'دکان کامیابی سے اپ ڈیٹ ہو گئی',
                'Shop deleted successfully': 'دکان کامیابی سے حذف ہو گئی',
                'User assigned to shop successfully': 'صارف دکان پر کامیابی سے تفویض ہو گیا',
                'Are you sure you want to delete this shop?': 'کیا آپ واقعی اس دکان کو حذف کرنا چاہتے ہیں؟',
                'All users will be unassigned from this shop.': 'تمام صارفین اس دکان سے غیر تفویض ہو جائیں گے۔',
                'No shops found': 'کوئی دکان نہیں ملی',
                'Loading shops...': 'دکانیں لوڈ ہو رہی ہیں...',
                'Shop name is required': 'دکان کا نام ضروری ہے',
                'Please select user and shop': 'براہ کرم صارف اور دکان منتخب کریں',
                'Success': 'کامیابی',
                'Error': 'خرابی',
                'Warning': 'انتباہ',
                'Users': 'صارفین',
                'Module': 'ماڈیول',
                'Business': 'کاروبار',
                'Created': 'تخلیق کردہ',
                'Dark Mode': 'ڈارک موڈ',
                'Light Mode': 'لائٹ موڈ',
                'Dashboard': 'ڈیش بورڈ',
                'Retail': 'ریٹیل',
                'Wholesale': 'تھوک',
                'Services': 'خدمات',
                'Manufacturing': 'مینوفیکچرنگ',
                'Total Users': 'کل صارفین',
                'Active Shops': 'فعال دکانیں',
                'Quick Stats': 'فوری اعدادوشمار',
                'Shop Details': 'دکان کی تفصیلات',
                'Shop Code': 'دکان کوڈ',
                'Address': 'پتہ',
                'Created At': 'تخلیق شدہ'
            },
            'ar': {
                'Shop Management': 'إدارة المتجر',
                'Manage Shops & Users': 'إدارة المتاجر والمستخدمين',
                'Create New Shop': 'إنشاء متجر جديد',
                'Edit Shop': 'تحرير المتجر',
                'Shop Name': 'اسم المتجر',
                'Business Module': 'وحدة الأعمال',
                'Shop Address': 'عنوان المتجر',
                'Theme Color': 'لون السمة',
                'Status': 'الحالة',
                'Active': 'نشط',
                'Inactive': 'غير نشط',
                'Created By': 'تم الإنشاء بواسطة',
                'Shop List': 'قائمة المتاجر',
                'Total Shops': 'إجمالي المتاجر',
                'Delete Shop': 'حذف المتجر',
                'Assign User': 'تعيين مستخدم',
                'Select User': 'اختر مستخدم',
                'Select Shop': 'اختر متجر',
                'Save Shop': 'حفظ المتجر',
                'Update Shop': 'تحديث المتجر',
                'Cancel': 'إلغاء',
                'Delete': 'حذف',
                'Edit': 'تحرير',
                'Creating...': 'جاري الإنشاء...',
                'Updating...': 'جاري التحديث...',
                'Assigning...': 'جاري التعيين...',
                'Shop created successfully': 'تم إنشاء المتجر بنجاح',
                'Shop updated successfully': 'تم تحديث المتجر بنجاح',
                'Shop deleted successfully': 'تم حذف المتجر بنجاح',
                'User assigned to shop successfully': 'تم تعيين المستخدم للمتجر بنجاح',
                'Are you sure you want to delete this shop?': 'هل أنت متأكد أنك تريد حذف هذا المتجر؟',
                'All users will be unassigned from this shop.': 'سيتم إلغاء تعيين جميع المستخدمين من هذا المتجر.',
                'No shops found': 'لم يتم العثور على متاجر',
                'Loading shops...': 'جاري تحميل المتاجر...',
                'Shop name is required': 'اسم المتجر مطلوب',
                'Please select user and shop': 'الرجاء اختيار المستخدم والمتجر',
                'Success': 'نجاح',
                'Error': 'خطأ',
                'Warning': 'تحذير',
                'Users': 'المستخدمين',
                'Module': 'الوحدة',
                'Business': 'الأعمال',
                'Created': 'تم الإنشاء',
                'Dark Mode': 'الوضع الداكن',
                'Light Mode': 'الوضع الفاتح',
                'Dashboard': 'لوحة القيادة',
                'Retail': 'تجزئة',
                'Wholesale': 'جملة',
                'Services': 'خدمات',
                'Manufacturing': 'تصنيع',
                'Total Users': 'إجمالي المستخدمين',
                'Active Shops': 'المتاجر النشطة',
                'Quick Stats': 'إحصائيات سريعة',
                'Shop Details': 'تفاصيل المتجر',
                'Shop Code': 'رمز المتجر',
                'Address': 'العنوان',
                'Created At': 'تم الإنشاء في'
            }
        };

        if (dict[targetLang] && dict[targetLang][text]) {
            return dict[targetLang][text];
        }

        try {
            const response = await fetch('https://translate.argosopentech.com/translate', {
                method: 'POST',
                body: JSON.stringify({ q: text, source: 'en', target: targetLang }),
                headers: { 'Content-Type': 'application/json' }
            });

            if (response.ok) {
                const data = await response.json();
                return data.translatedText;
            }
        } catch (e) {
            console.log("LibreTranslate failed");
        }

        try {
            const response = await fetch(
                `https://api.mymemory.translated.net/get?q=${encodeURIComponent(text)}&langpair=en|${targetLang}`
            );
            if (response.ok) {
                const data = await response.json();
                return data.responseData.translatedText;
            }
        } catch (e) {
            console.log("MyMemory translation failed");
        }

        return text;
    };

    const translateText = async (text, targetLang) => {
        const cacheKey = `${text}_${targetLang}`;

        if (cache.has(cacheKey)) {
            return cache.get(cacheKey);
        }

        const translated = await translateAPI(text, targetLang);
        cache.set(cacheKey, translated);
        saveToCache();

        return translated;
    };

    const defaultTexts = {
        shopManagement: 'Shop Management',
        manageShops: 'Manage Shops & Users',
        createShop: 'Create New Shop',
        editShop: 'Edit Shop',
        shopName: 'Shop Name',
        businessModule: 'Business Module',
        shopAddress: 'Shop Address',
        themeColor: 'Theme Color',
        status: 'Status',
        active: 'Active',
        inactive: 'Inactive',
        createdBy: 'Created By',
        shopList: 'Shop List',
        totalShops: 'Total Shops',
        deleteShop: 'Delete Shop',
        assignUser: 'Assign User',
        selectUser: 'Select User',
        selectShop: 'Select Shop',
        saveShop: 'Save Shop',
        updateShop: 'Update Shop',
        cancel: 'Cancel',
        delete: 'Delete',
        edit: 'Edit',
        creating: 'Creating...',
        updating: 'Updating...',
        assigning: 'Assigning...',
        shopCreated: 'Shop created successfully',
        shopUpdated: 'Shop updated successfully',
        shopDeleted: 'Shop deleted successfully',
        userAssigned: 'User assigned to shop successfully',
        confirmDelete: 'Are you sure you want to delete this shop?',
        deleteWarning: 'All users will be unassigned from this shop.',
        noShopsFound: 'No shops found',
        loadingShops: 'Loading shops...',
        shopNameRequired: 'Shop name is required',
        selectUserRequired: 'Please select user and shop',
        success: 'Success',
        error: 'Error',
        warning: 'Warning',
        users: 'Users',
        module: 'Module',
        business: 'Business',
        created: 'Created',
        darkMode: 'Dark Mode',
        lightMode: 'Light Mode',
        dashboard: 'Dashboard',
        retail: 'Retail',
        wholesale: 'Wholesale',
        services: 'Services',
        manufacturing: 'Manufacturing',
        totalUsers: 'Total Users',
        activeShops: 'Active Shops',
        quickStats: 'Quick Stats',
        shopDetails: 'Shop Details',
        shopCode: 'Shop Code',
        address: 'Address',
        createdAt: 'Created At'
    };

    const getTranslations = async (lang) => {
        if (lang === 'en') {
            return defaultTexts;
        }

        const translations = {};
        const promises = [];

        Object.keys(defaultTexts).forEach(key => {
            const cacheKey = `${defaultTexts[key]}_${lang}`;
            if (cache.has(cacheKey)) {
                translations[key] = cache.get(cacheKey);
            } else {
                promises.push(
                    translateText(defaultTexts[key], lang).then(translated => {
                        translations[key] = translated;
                    })
                );
            }
        });

        if (promises.length > 0) {
            await Promise.all(promises);
            saveToCache();
        }

        return translations;
    };

    const addListener = (callback) => {
        translationListeners.add(callback);
    };

    const removeListener = (callback) => {
        translationListeners.delete(callback);
    };

    loadFromCache();

    return {
        getTranslations,
        translateText,
        addListener,
        removeListener
    };
};

const ozShopsTranslator = createOZShopsTranslator();

// ==============================================
// Toast Notification Component
// ==============================================
const OZShopsToast = ({ toast, setToast, translations, darkMode }) => {
    const getIconForType = (type) => {
        switch (type) {
            case 'success': return faCheckCircle;
            case 'error': return faExclamationTriangle;
            case 'warning': return faExclamationTriangle;
            default: return faCheckCircle;
        }
    };

    const getTitleForType = (type) => {
        switch (type) {
            case 'success': return translations?.success || 'Success';
            case 'error': return translations?.error || 'Error';
            case 'warning': return translations?.warning || 'Warning';
            default: return '';
        }
    };

    if (!toast.show) return null;

    const isRTL = document.documentElement.dir === 'rtl';

    return (
        <div className={`ozshops-toast ozshops-toast-${toast.type} ${isRTL ? 'ozshops-toast-rtl' : ''} ${darkMode ? 'ozshops-toast-dark' : ''}`}>
            <div className="ozshops-toast-content">
                <div className="ozshops-toast-icon">
                    <FontAwesomeIcon icon={getIconForType(toast.type)} />
                </div>
                <div className="ozshops-toast-text">
                    <div className="ozshops-toast-title">{getTitleForType(toast.type)}</div>
                    <div className="ozshops-toast-message">{toast.message}</div>
                </div>
                <button className="ozshops-toast-close" onClick={() => setToast(prev => ({ ...prev, show: false }))}>
                    <FontAwesomeIcon icon={faTimes} />
                </button>
            </div>
            <div className="ozshops-toast-timer">
                <div className="ozshops-toast-progress" style={{ width: `${toast.progress}%` }}></div>
            </div>
        </div>
    );
};

// ==============================================
// Main ShopManagement Component
// ==============================================
const ShopsManagement = () => {
    const { user } = useAuth();
    
    // State
    const [shops, setShops] = useState([]);
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    
    // Dark Mode State
    const [darkMode, setDarkMode] = useState(() => {
        return localStorage.getItem('ourzest_darkMode') === 'true';
    });
    
    // Language and Translation
    const [currentLanguage, setCurrentLanguage] = useState(() => {
        return localStorage.getItem('ourzest_language') || 'en';
    });
    const [translations, setTranslations] = useState({});
    
    // Theme color
    const [themeColor, setThemeColor] = useState(() => {
        return localStorage.getItem('ourzest_theme') || '#6366f1';
    });
    
    // Form states
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [shopToDelete, setShopToDelete] = useState(null);
    const [shopToEdit, setShopToEdit] = useState(null);
    const [creatingShop, setCreatingShop] = useState(false);
    const [updatingShop, setUpdatingShop] = useState(false);
    const [assigningUser, setAssigningUser] = useState(false);
    
    // Form data
    const [shopForm, setShopForm] = useState({
        shop_name: '',
        business_module: 'dashboard',
        shop_address: '',
        themecolor: '#6366f1'
    });
    
    const [editForm, setEditForm] = useState({
        shop_id: '',
        shop_name: '',
        business_module: 'dashboard',
        shop_address: '',
        themecolor: '#6366f1',
        status: 'active'
    });
    
    const [assignForm, setAssignForm] = useState({
        user_id: '',
        shop_id: ''
    });
    
    // Toast state
    const [toast, setToast] = useState({
        show: false,
        type: '',
        message: '',
        progress: 100,
        timeLeft: 3
    });
    
    const toastTimerRef = useRef(null);

    // ==============================================
    // DARK MODE AUTO-SYNC
    // ==============================================
    useEffect(() => {
        const syncDarkMode = () => {
            const isDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            setDarkMode(isDarkMode);
            
            const container = document.querySelector('.ozshops-container');
            if (container) {
                if (isDarkMode) {
                    container.classList.add('ozshops-dark');
                } else {
                    container.classList.remove('ozshops-dark');
                }
            }
        };
        
        syncDarkMode();
        
        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_darkMode') {
                syncDarkMode();
            }
        };
        
        const handleDarkModeToggle = (event) => {
            if (event.detail) {
                const isDark = event.detail.darkMode;
                setDarkMode(isDark);
                const container = document.querySelector('.ozshops-container');
                if (container) {
                    if (isDark) {
                        container.classList.add('ozshops-dark');
                    } else {
                        container.classList.remove('ozshops-dark');
                    }
                }
            }
        };
        
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('darkModeToggled', handleDarkModeToggle);
        
        const interval = setInterval(() => {
            const storedDarkMode = localStorage.getItem('ourzest_darkMode') === 'true';
            if (storedDarkMode !== darkMode) {
                syncDarkMode();
            }
        }, 1000);
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('darkModeToggled', handleDarkModeToggle);
            clearInterval(interval);
        };
    }, [darkMode]);

    // ==============================================
    // THEME COLOR AUTO-SYNC
    // ==============================================
    const applyTheme = useCallback((color) => {
        if (!color || !/^#[0-9A-F]{6}$/i.test(color)) {
            color = '#6366f1';
        }
        
        document.documentElement.style.setProperty('--ozshops-primary', color);
        document.documentElement.style.setProperty('--ozshops-primary-dark', color);
        document.documentElement.style.setProperty('--ozshops-primary-light', color);
        
        setThemeColor(color);
    }, []);

    useEffect(() => {
        const savedTheme = localStorage.getItem('ourzest_theme') || '#6366f1';
        applyTheme(savedTheme);
        
        const handleStorageChange = (e) => {
            if (e.key === 'ourzest_theme') {
                applyTheme(e.newValue || '#6366f1');
            }
        };
        
        const handleThemeChanged = (event) => {
            if (event.detail && event.detail.theme) {
                applyTheme(event.detail.theme);
            }
        };
        
        window.addEventListener('storage', handleStorageChange);
        window.addEventListener('themeChanged', handleThemeChanged);
        
        const interval = setInterval(() => {
            const storedTheme = localStorage.getItem('ourzest_theme');
            if (storedTheme && storedTheme !== themeColor) {
                applyTheme(storedTheme);
            }
        }, 1000);
        
        return () => {
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('themeChanged', handleThemeChanged);
            clearInterval(interval);
        };
    }, [applyTheme, themeColor]);

    // ==============================================
    // LANGUAGE AUTO-SYNC
    // ==============================================
    useEffect(() => {
        const loadTranslations = async (lang) => {
            try {
                const trans = await ozShopsTranslator.getTranslations(lang);
                setTranslations(trans);
            } catch (error) {
                console.error('Error loading translations:', error);
            }
        };
        
        const handleLanguageChange = () => {
            const lang = localStorage.getItem('ourzest_language') || 'en';
            if (lang !== currentLanguage) {
                setCurrentLanguage(lang);
                loadTranslations(lang);
            }
        };
        
        const handleCustomLanguageChange = (event) => {
            if (event.detail && event.detail.language) {
                const newLang = event.detail.language;
                if (newLang !== currentLanguage) {
                    setCurrentLanguage(newLang);
                    loadTranslations(newLang);
                }
            }
        };
        
        loadTranslations(currentLanguage);
        
        window.addEventListener('storage', handleLanguageChange);
        window.addEventListener('languageChanged', handleCustomLanguageChange);
        
        const interval = setInterval(() => {
            const storedLang = localStorage.getItem('ourzest_language') || 'en';
            if (storedLang !== currentLanguage) {
                setCurrentLanguage(storedLang);
                loadTranslations(storedLang);
            }
        }, 1000);
        
        return () => {
            window.removeEventListener('storage', handleLanguageChange);
            window.removeEventListener('languageChanged', handleCustomLanguageChange);
            clearInterval(interval);
        };
    }, [currentLanguage]);

    // ==============================================
    // RTL DIRECTION SYNC
    // ==============================================
    useEffect(() => {
        const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';
        const container = document.querySelector('.ozshops-container');
        
        if (container) {
            if (isRTL) {
                container.classList.add('ozshops-rtl');
            } else {
                container.classList.remove('ozshops-rtl');
            }
        }
        
        if (isRTL) {
            document.documentElement.dir = 'rtl';
            document.body.dir = 'rtl';
        } else {
            document.documentElement.dir = 'ltr';
            document.body.dir = 'ltr';
        }
    }, [currentLanguage]);

    // ==============================================
    // Toast Functions
    // ==============================================
    const showToast = useCallback((type, message) => {
        if (toastTimerRef.current) {
            clearInterval(toastTimerRef.current);
        }
        
        setToast({
            show: true,
            type,
            message,
            progress: 100,
            timeLeft: 3
        });

        let timeLeft = 3;
        const timer = setInterval(() => {
            timeLeft -= 0.1;
            const progress = (timeLeft / 3) * 100;
            
            setToast(prev => ({
                ...prev,
                progress,
                timeLeft: parseFloat(timeLeft.toFixed(1))
            }));

            if (timeLeft <= 0) {
                clearInterval(timer);
                setToast(prev => ({ ...prev, show: false }));
            }
        }, 100);

        toastTimerRef.current = timer;
    }, []);

    useEffect(() => {
        return () => {
            if (toastTimerRef.current) {
                clearInterval(toastTimerRef.current);
            }
        };
    }, []);

    // ==============================================
    // Data Fetching
    // ==============================================
    const fetchShops = async () => {
        setLoading(true);
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/shops.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ action: 'get_shops' })
            });

            const data = await response.json();
            
            if (data.status === 'success') {
                setShops(data.data || []);
            } else {
                showToast('error', data.message || 'Failed to fetch shops');
                setShops([]);
            }
        } catch (error) {
            console.error('Error fetching shops:', error);
            showToast('error', error.message || 'Network error');
            setShops([]);
        } finally {
            setLoading(false);
        }
    };

    const fetchUsers = async () => {
        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/root_users.php?action=list');
            const data = await response.json();
            
            if (data.success) {
                setUsers(data.data || []);
            }
        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };

    useEffect(() => {
        if (user) {
            fetchShops();
            fetchUsers();
        }
    }, [user]);

    // ==============================================
    // Form Handlers
    // ==============================================
    const handleShopFormChange = (e) => {
        const { name, value } = e.target;
        setShopForm(prev => ({ ...prev, [name]: value }));
    };

    const handleEditFormChange = (e) => {
        const { name, value } = e.target;
        setEditForm(prev => ({ ...prev, [name]: value }));
    };

    const handleAssignFormChange = (e) => {
        const { name, value } = e.target;
        setAssignForm(prev => ({ ...prev, [name]: value }));
    };

    const handleEditClick = (shop) => {
        setShopToEdit(shop);
        setEditForm({
            shop_id: shop.shop_id,
            shop_name: shop.shop_name,
            business_module: shop.business_module || 'dashboard',
            shop_address: shop.shop_address || '',
            themecolor: shop.themecolor || '#6366f1',
            status: shop.status || 'active'
        });
        setShowEditModal(true);
    };

    const handleCreateShop = async (e) => {
        e.preventDefault();
        
        if (!shopForm.shop_name.trim()) {
            showToast('error', translations.shopNameRequired || 'Shop name is required');
            return;
        }

        setCreatingShop(true);

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/shops.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'create_shop',
                    shop_name: shopForm.shop_name,
                    business_module: shopForm.business_module,
                    shop_address: shopForm.shop_address || '',
                    themecolor: shopForm.themecolor,
                    created_by: user.id
                })
            });

            const data = await response.json();
            
            if (data.status === 'success') {
                showToast('success', translations.shopCreated || 'Shop created successfully');
                setShowCreateModal(false);
                setShopForm({ shop_name: '', business_module: 'dashboard', shop_address: '', themecolor: '#6366f1' });
                await fetchShops();
            } else {
                showToast('error', data.message || 'Failed to create shop');
            }
        } catch (error) {
            showToast('error', 'Network error occurred');
        } finally {
            setCreatingShop(false);
        }
    };

    const handleUpdateShop = async (e) => {
        e.preventDefault();
        
        if (!editForm.shop_name.trim()) {
            showToast('error', translations.shopNameRequired || 'Shop name is required');
            return;
        }

        setUpdatingShop(true);

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/shops.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'update_shop',
                    shop_id: editForm.shop_id,
                    shop_name: editForm.shop_name,
                    business_module: editForm.business_module,
                    shop_address: editForm.shop_address || '',
                    themecolor: editForm.themecolor,
                    status: editForm.status
                })
            });

            const data = await response.json();
            
            if (data.status === 'success') {
                showToast('success', translations.shopUpdated || 'Shop updated successfully');
                setShowEditModal(false);
                await fetchShops();
            } else {
                showToast('error', data.message || 'Failed to update shop');
            }
        } catch (error) {
            showToast('error', 'Network error occurred');
        } finally {
            setUpdatingShop(false);
        }
    };

    const handleAssignUser = async (e) => {
        e.preventDefault();
        
        if (!assignForm.user_id || !assignForm.shop_id) {
            showToast('error', translations.selectUserRequired || 'Please select user and shop');
            return;
        }

        setAssigningUser(true);

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/shops.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'assign_user',
                    user_id: assignForm.user_id,
                    shop_id: assignForm.shop_id,
                    assigned_by: user.id
                })
            });

            const data = await response.json();
            
            if (data.status === 'success') {
                showToast('success', translations.userAssigned || 'User assigned successfully');
                setShowAssignModal(false);
                setAssignForm({ user_id: '', shop_id: '' });
                await Promise.all([fetchShops(), fetchUsers()]);
            } else {
                showToast('error', data.message || 'Failed to assign user');
            }
        } catch (error) {
            showToast('error', 'Network error occurred');
        } finally {
            setAssigningUser(false);
        }
    };

    const handleDeleteShop = async () => {
        if (!shopToDelete) return;

        try {
            const response = await fetch('https://ourzestapp.online/oz_backend/shops.php', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    action: 'delete_shop',
                    shop_id: shopToDelete
                })
            });

            const data = await response.json();
            
            if (data.status === 'success') {
                showToast('success', translations.shopDeleted || 'Shop deleted successfully');
                setShowDeleteModal(false);
                setShopToDelete(null);
                await Promise.all([fetchShops(), fetchUsers()]);
            } else {
                showToast('error', data.message || 'Failed to delete shop');
            }
        } catch (error) {
            showToast('error', 'Network error occurred');
        }
    };

    const getShopUsersCount = (shopId) => {
        return users.filter(u => u.shop_id == shopId).length;
    };

    const activeShopsCount = shops.filter(s => s.status === 'active').length;
    const totalUsersCount = users.length;

    const moduleIcons = {
        dashboard: faChartLine,
        retail: faShoppingCart,
        wholesale: faBoxes,
        services: faCog,
        manufacturing: faWarehouse
    };

    const isRTL = currentLanguage === 'ur' || currentLanguage === 'ar';

    const moduleOptions = [
        { value: 'dashboard', label: translations.dashboard || 'Dashboard', icon: faChartLine },
        { value: 'retail', label: translations.retail || 'Retail', icon: faShoppingCart },
        { value: 'wholesale', label: translations.wholesale || 'Wholesale', icon: faBoxes },
        { value: 'services', label: translations.services || 'Services', icon: faCog },
        { value: 'manufacturing', label: translations.manufacturing || 'Manufacturing', icon: faWarehouse }
    ];

    return (
        <div className={`ozshops-container ${isRTL ? 'ozshops-rtl' : ''} ${darkMode ? 'ozshops-dark' : ''}`}>
            <OZShopsToast toast={toast} setToast={setToast} translations={translations} darkMode={darkMode} />

            {/* Header Section */}
            <div className="ozshops-header">
                <div className="ozshops-header-bg-animation">
                    <div className="ozshops-orb ozshops-orb-1"></div>
                    <div className="ozshops-orb ozshops-orb-2"></div>
                    <div className="ozshops-orb ozshops-orb-3"></div>
                    <div className="ozshops-orb ozshops-orb-4"></div>
                    <div className="ozshops-orb ozshops-orb-5"></div>
                </div>
                <div className="ozshops-header-content">
                    <div className="ozshops-header-left">
                        <div className="ozshops-header-icon">
                            <FontAwesomeIcon icon={faStoreAlt} />
                        </div>
                        <div className="ozshops-header-text">
                            <h1>{translations.shopManagement || 'Shop Management'}</h1>
                            <p>{translations.manageShops || 'Manage Shops & Users'}</p>
                        </div>
                    </div>
                    <div className="ozshops-header-right">
                        <button className="ozshops-btn ozshops-btn-primary" onClick={() => setShowCreateModal(true)} disabled={loading}>
                            <FontAwesomeIcon icon={faPlus} />
                            <span>{translations.createShop || 'Create Shop'}</span>
                        </button>
                        <button className="ozshops-btn ozshops-btn-secondary" onClick={() => {
                            if (shops.length === 0) showToast('warning', translations.noShopsFound || 'No shops found');
                            else if (users.length === 0) showToast('warning', 'No users available');
                            else setShowAssignModal(true);
                        }} disabled={loading}>
                            <FontAwesomeIcon icon={faUserPlus} />
                            <span>{translations.assignUser || 'Assign User'}</span>
                        </button>
                    </div>
                </div>
            </div>

            {/* Stats Cards */}
            <div className="ozshops-stats-grid">
                <div className="ozshops-stat-card">
                    <div className="ozshops-stat-icon ozshops-stat-icon-primary">
                        <FontAwesomeIcon icon={faStore} />
                    </div>
                    <div className="ozshops-stat-info">
                        <h3>{shops.length}</h3>
                        <p>{translations.totalShops || 'Total Shops'}</p>
                    </div>
                </div>
                <div className="ozshops-stat-card">
                    <div className="ozshops-stat-icon ozshops-stat-icon-success">
                        <FontAwesomeIcon icon={faCheckCircle} />
                    </div>
                    <div className="ozshops-stat-info">
                        <h3>{activeShopsCount}</h3>
                        <p>{translations.activeShops || 'Active Shops'}</p>
                    </div>
                </div>
                <div className="ozshops-stat-card">
                    <div className="ozshops-stat-icon ozshops-stat-icon-info">
                        <FontAwesomeIcon icon={faUsers} />
                    </div>
                    <div className="ozshops-stat-info">
                        <h3>{totalUsersCount}</h3>
                        <p>{translations.totalUsers || 'Total Users'}</p>
                    </div>
                </div>
            </div>

            {/* Shops List */}
            <div className="ozshops-list-container">
                <div className="ozshops-list-header">
                    <h2 className="ozshops-section-title">
                        <FontAwesomeIcon icon={faStore} />
                        <span>{translations.shopList || 'Shop List'}</span>
                        <span className="ozshops-count-badge">{shops.length}</span>
                    </h2>
                </div>

                {loading ? (
                    <div className="ozshops-loading">
                        <div className="ozshops-loading-spinner"></div>
                        <p>{translations.loadingShops || 'Loading shops...'}</p>
                    </div>
                ) : shops.length === 0 ? (
                    <div className="ozshops-empty">
                        <FontAwesomeIcon icon={faStore} />
                        <p>{translations.noShopsFound || 'No shops found'}</p>
                        <button className="ozshops-btn ozshops-btn-primary" onClick={() => setShowCreateModal(true)}>
                            <FontAwesomeIcon icon={faPlus} />
                            {translations.createShop || 'Create Your First Shop'}
                        </button>
                    </div>
                ) : (
                    <div className="ozshops-grid">
                        {shops.map((shop) => (
                            <div key={shop.shop_id} className="ozshops-card">
                                <div className="ozshops-card-header">
                                    <div className="ozshops-card-icon">
                                        <FontAwesomeIcon icon={moduleIcons[shop.business_module] || faStore} />
                                    </div>
                                    <div className="ozshops-card-title">
                                        <h3>{shop.shop_name}</h3>
                                        <span className="ozshops-card-code">
                                            <FontAwesomeIcon icon={faHashtag} />
                                            {shop.shop_code}
                                        </span>
                                    </div>
                                    <div className="ozshops-card-status">
                                        <span className={`ozshops-status-badge ozshops-status-${shop.status}`}>
                                            {shop.status === 'active' ? (translations.active || 'Active') : (translations.inactive || 'Inactive')}
                                        </span>
                                    </div>
                                </div>
                                
                                <div className="ozshops-card-body">
                                    <div className="ozshops-card-row">
                                        <FontAwesomeIcon icon={faUsers} />
                                        <span><strong>{translations.users || 'Users'}:</strong> {getShopUsersCount(shop.shop_id)}</span>
                                    </div>
                                    <div className="ozshops-card-row">
                                        <FontAwesomeIcon icon={faUser} />
                                        <span><strong>{translations.createdBy || 'Created By'}:</strong> {shop.created_by_username || 'Admin'}</span>
                                    </div>
                                    <div className="ozshops-card-row">
                                        <FontAwesomeIcon icon={faIdCard} />
                                        <span><strong>{translations.module || 'Module'}:</strong> {shop.business_module || 'dashboard'}</span>
                                    </div>
                                    {shop.shop_address && (
                                        <div className="ozshops-card-row">
                                            <FontAwesomeIcon icon={faMapMarkerAlt} />
                                            <span><strong>{translations.address || 'Address'}:</strong> {shop.shop_address}</span>
                                        </div>
                                    )}
                                    <div className="ozshops-card-row">
                                        <FontAwesomeIcon icon={faCalendarAlt} />
                                        <span><strong>{translations.createdAt || 'Created'}:</strong> {new Date(shop.created_at).toLocaleDateString()}</span>
                                    </div>
                                </div>
                                
                                <div className="ozshops-card-footer">
                                    <button className="ozshops-action-btn ozshops-action-edit" onClick={() => handleEditClick(shop)} title={translations.edit || 'Edit'}>
                                        <FontAwesomeIcon icon={faEdit} />
                                    </button>
                                    <button className="ozshops-action-btn ozshops-action-delete" onClick={() => { setShopToDelete(shop.shop_id); setShowDeleteModal(true); }} title={translations.delete || 'Delete'}>
                                        <FontAwesomeIcon icon={faTrash} />
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Create Shop Modal */}
            {showCreateModal && (
                <div className="ozshops-modal-overlay" onClick={() => !creatingShop && setShowCreateModal(false)}>
                    <div className="ozshops-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="ozshops-modal-header">
                            <div className="ozshops-modal-icon">
                                <FontAwesomeIcon icon={faPlus} />
                            </div>
                            <h3>{translations.createShop || 'Create New Shop'}</h3>
                            <button className="ozshops-modal-close" onClick={() => !creatingShop && setShowCreateModal(false)} disabled={creatingShop}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <form onSubmit={handleCreateShop} className="ozshops-form">
                            <div className="ozshops-form-group">
                                <label className="ozshops-form-label">{translations.shopName || 'Shop Name'} <span className="ozshops-required">*</span></label>
                                <div className="ozshops-input-wrapper">
                                    <FontAwesomeIcon icon={faStore} className="ozshops-input-icon" />
                                    <input type="text" name="shop_name" value={shopForm.shop_name} onChange={handleShopFormChange} placeholder=" " disabled={creatingShop} required />
                                    <label className="ozshops-floating-label">Enter shop name</label>
                                </div>
                            </div>
                            <div className="ozshops-form-group">
                                <label className="ozshops-form-label">{translations.businessModule || 'Business Module'}</label>
                                <div className="ozshops-input-wrapper">
                                    <FontAwesomeIcon icon={faIdCard} className="ozshops-input-icon" />
                                    <select name="business_module" value={shopForm.business_module} onChange={handleShopFormChange} disabled={creatingShop}>
                                        {moduleOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="ozshops-form-group ozshops-form-group-full">
                                <label className="ozshops-form-label">{translations.shopAddress || 'Shop Address'}</label>
                                <div className="ozshops-input-wrapper">
                                    <FontAwesomeIcon icon={faMapMarkerAlt} className="ozshops-input-icon" />
                                    <textarea name="shop_address" value={shopForm.shop_address} onChange={handleShopFormChange} rows="3" placeholder=" " disabled={creatingShop}></textarea>
                                    <label className="ozshops-floating-label">Enter shop address (optional)</label>
                                </div>
                            </div>
                            <div className="ozshops-form-group">
                                <label className="ozshops-form-label">{translations.themeColor || 'Theme Color'}</label>
                                <div className="ozshops-input-wrapper">
                                    <FontAwesomeIcon icon={faPalette} className="ozshops-input-icon" />
                                    <input type="color" name="themecolor" value={shopForm.themecolor} onChange={handleShopFormChange} disabled={creatingShop} />
                                </div>
                            </div>
                            <div className="ozshops-form-actions">
                                <button type="button" className="ozshops-btn ozshops-btn-secondary" onClick={() => setShowCreateModal(false)} disabled={creatingShop}>
                                    <FontAwesomeIcon icon={faTimes} /> {translations.cancel || 'Cancel'}
                                </button>
                                <button type="submit" className="ozshops-btn ozshops-btn-primary" disabled={creatingShop}>
                                    {creatingShop ? <><div className="ozshops-btn-spinner"></div><span>{translations.creating || 'Creating...'}</span></> : <><FontAwesomeIcon icon={faSave} /> {translations.saveShop || 'Save Shop'}</>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Edit Shop Modal */}
            {showEditModal && shopToEdit && (
                <div className="ozshops-modal-overlay" onClick={() => !updatingShop && setShowEditModal(false)}>
                    <div className="ozshops-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="ozshops-modal-header">
                            <div className="ozshops-modal-icon">
                                <FontAwesomeIcon icon={faEdit} />
                            </div>
                            <h3>{translations.editShop || 'Edit Shop'}: {shopToEdit.shop_name}</h3>
                            <button className="ozshops-modal-close" onClick={() => !updatingShop && setShowEditModal(false)} disabled={updatingShop}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <form onSubmit={handleUpdateShop} className="ozshops-form">
                            <div className="ozshops-form-group">
                                <label className="ozshops-form-label">{translations.shopName || 'Shop Name'} <span className="ozshops-required">*</span></label>
                                <div className="ozshops-input-wrapper">
                                    <FontAwesomeIcon icon={faStore} className="ozshops-input-icon" />
                                    <input type="text" name="shop_name" value={editForm.shop_name} onChange={handleEditFormChange} placeholder=" " disabled={updatingShop} required />
                                    <label className="ozshops-floating-label">Enter shop name</label>
                                </div>
                            </div>
                            <div className="ozshops-form-group">
                                <label className="ozshops-form-label">{translations.businessModule || 'Business Module'}</label>
                                <div className="ozshops-input-wrapper">
                                    <FontAwesomeIcon icon={faIdCard} className="ozshops-input-icon" />
                                    <select name="business_module" value={editForm.business_module} onChange={handleEditFormChange} disabled={updatingShop}>
                                        {moduleOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                    </select>
                                </div>
                            </div>
                            <div className="ozshops-form-group ozshops-form-group-full">
                                <label className="ozshops-form-label">{translations.shopAddress || 'Shop Address'}</label>
                                <div className="ozshops-input-wrapper">
                                    <FontAwesomeIcon icon={faMapMarkerAlt} className="ozshops-input-icon" />
                                    <textarea name="shop_address" value={editForm.shop_address} onChange={handleEditFormChange} rows="3" placeholder=" " disabled={updatingShop}></textarea>
                                    <label className="ozshops-floating-label">Enter shop address (optional)</label>
                                </div>
                            </div>
                            <div className="ozshops-form-group">
                                <label className="ozshops-form-label">{translations.themeColor || 'Theme Color'}</label>
                                <div className="ozshops-input-wrapper">
                                    <FontAwesomeIcon icon={faPalette} className="ozshops-input-icon" />
                                    <input type="color" name="themecolor" value={editForm.themecolor} onChange={handleEditFormChange} disabled={updatingShop} />
                                </div>
                            </div>
                            <div className="ozshops-form-group">
                                <label className="ozshops-form-label">{translations.status || 'Status'}</label>
                                <div className="ozshops-input-wrapper">
                                    <FontAwesomeIcon icon={faCheckCircle} className="ozshops-input-icon" />
                                    <select name="status" value={editForm.status} onChange={handleEditFormChange} disabled={updatingShop}>
                                        <option value="active">{translations.active || 'Active'}</option>
                                        <option value="inactive">{translations.inactive || 'Inactive'}</option>
                                    </select>
                                </div>
                            </div>
                            <div className="ozshops-form-actions">
                                <button type="button" className="ozshops-btn ozshops-btn-secondary" onClick={() => setShowEditModal(false)} disabled={updatingShop}>
                                    <FontAwesomeIcon icon={faTimes} /> {translations.cancel || 'Cancel'}
                                </button>
                                <button type="submit" className="ozshops-btn ozshops-btn-primary" disabled={updatingShop}>
                                    {updatingShop ? <><div className="ozshops-btn-spinner"></div><span>{translations.updating || 'Updating...'}</span></> : <><FontAwesomeIcon icon={faSave} /> {translations.updateShop || 'Update Shop'}</>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Assign User Modal */}
            {showAssignModal && (
                <div className="ozshops-modal-overlay" onClick={() => !assigningUser && setShowAssignModal(false)}>
                    <div className="ozshops-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="ozshops-modal-header">
                            <div className="ozshops-modal-icon">
                                <FontAwesomeIcon icon={faUserPlus} />
                            </div>
                            <h3>{translations.assignUser || 'Assign User to Shop'}</h3>
                            <button className="ozshops-modal-close" onClick={() => !assigningUser && setShowAssignModal(false)} disabled={assigningUser}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <form onSubmit={handleAssignUser} className="ozshops-form">
                            <div className="ozshops-form-group">
                                <label className="ozshops-form-label">{translations.selectUser || 'Select User'} <span className="ozshops-required">*</span></label>
                                <div className="ozshops-input-wrapper">
                                    <FontAwesomeIcon icon={faUser} className="ozshops-input-icon" />
                                    <select name="user_id" value={assignForm.user_id} onChange={handleAssignFormChange} required disabled={assigningUser}>
                                        <option value="">{translations.selectUser || 'Select User'}</option>
                                        {users.filter(u => u.status === 'active').map(user => (
                                            <option key={user.id} value={user.id}>{user.username} - {user.fullname} ({user.role})</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="ozshops-form-group">
                                <label className="ozshops-form-label">{translations.selectShop || 'Select Shop'} <span className="ozshops-required">*</span></label>
                                <div className="ozshops-input-wrapper">
                                    <FontAwesomeIcon icon={faStore} className="ozshops-input-icon" />
                                    <select name="shop_id" value={assignForm.shop_id} onChange={handleAssignFormChange} required disabled={assigningUser}>
                                        <option value="">{translations.selectShop || 'Select Shop'}</option>
                                        {shops.map(shop => (
                                            <option key={shop.shop_id} value={shop.shop_id}>{shop.shop_name} ({shop.shop_code})</option>
                                        ))}
                                    </select>
                                </div>
                            </div>
                            <div className="ozshops-form-actions">
                                <button type="button" className="ozshops-btn ozshops-btn-secondary" onClick={() => setShowAssignModal(false)} disabled={assigningUser}>
                                    <FontAwesomeIcon icon={faTimes} /> {translations.cancel || 'Cancel'}
                                </button>
                                <button type="submit" className="ozshops-btn ozshops-btn-primary" disabled={assigningUser}>
                                    {assigningUser ? <><div className="ozshops-btn-spinner"></div><span>{translations.assigning || 'Assigning...'}</span></> : <><FontAwesomeIcon icon={faCheck} /> {translations.assignUser || 'Assign User'}</>}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Delete Modal */}
            {showDeleteModal && (
                <div className="ozshops-modal-overlay" onClick={() => setShowDeleteModal(false)}>
                    <div className="ozshops-modal ozshops-modal-delete" onClick={(e) => e.stopPropagation()}>
                        <div className="ozshops-modal-header ozshops-modal-header-danger">
                            <div className="ozshops-modal-icon">
                                <FontAwesomeIcon icon={faExclamationTriangle} />
                            </div>
                            <h3>{translations.confirmDelete || 'Confirm Delete'}</h3>
                            <button className="ozshops-modal-close" onClick={() => setShowDeleteModal(false)}>
                                <FontAwesomeIcon icon={faTimes} />
                            </button>
                        </div>
                        <div className="ozshops-modal-body">
                            <p>{translations.confirmDelete || 'Are you sure you want to delete this shop?'}</p>
                            <p className="ozshops-warning-text"><FontAwesomeIcon icon={faExclamationTriangle} /> {translations.deleteWarning || 'All users will be unassigned from this shop.'}</p>
                        </div>
                        <div className="ozshops-form-actions">
                            <button type="button" className="ozshops-btn ozshops-btn-secondary" onClick={() => setShowDeleteModal(false)}>
                                <FontAwesomeIcon icon={faTimes} /> {translations.cancel || 'Cancel'}
                            </button>
                            <button type="button" className="ozshops-btn ozshops-btn-danger" onClick={handleDeleteShop}>
                                <FontAwesomeIcon icon={faTrash} /> {translations.delete || 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ShopsManagement;